import { computeAgeYears, degressiveFactor, estimateJumpsFromAge, roundToNearest } from './gvMath';
import { MANUFACTURERS, DEPRECIATION, ADJUSTMENTS } from '../config/containerConfig';

const ageDepreciationEur = (ageYears, newPriceEur, cfg) => {
  const { knee_years, knee_power, knee_value_eur, phase1_end_years, cliff_power, transition_value_eur, phase2_end_years, floor_value_eur } = cfg;

  if (ageYears <= 0) return newPriceEur;

  if (ageYears <= knee_years) {
    // Phase 1a: regressive - gentle early, accelerating
    const t = ageYears / knee_years;
    return newPriceEur - (newPriceEur - knee_value_eur) * Math.pow(t, knee_power);
  }

  if (ageYears <= phase1_end_years) {
    // Phase 1b: cliff - decelerating into phase 2
    const t = (ageYears - knee_years) / (phase1_end_years - knee_years);
    return transition_value_eur + (knee_value_eur - transition_value_eur) * Math.pow(1 - t, cliff_power);
  }

  if (ageYears <= phase2_end_years) {
    // Phase 2: linear from transition to floor
    const t = (ageYears - phase1_end_years) / (phase2_end_years - phase1_end_years);
    return transition_value_eur + (floor_value_eur - transition_value_eur) * t;
  }

  return floor_value_eur;
};

export const calculateContainer = ({ manufacturer, model, domYear, jumps, maxCanopySqft, minCanopySqft, heightCm, weightKg, customNewPriceEur }) => {
  const notes = [];
  const adjustments = {};
  let newPriceEur;
  let mfrCfg = null;
  let brandKey;

  if (customNewPriceEur) {
    newPriceEur = customNewPriceEur;
    brandKey = null;
    notes.push('Custom price used. Brand and version adjustments not applied.');
  } else {
    const mfrKey = manufacturer.toLowerCase().trim();
    const mdlKey = model.toLowerCase().trim();
    mfrCfg = MANUFACTURERS[mfrKey];
    if (!mfrCfg) {
      throw new Error(`Unknown manufacturer: ${manufacturer}. Expected: ${Object.keys(MANUFACTURERS).join(', ')}`);
    }
    const modelCfg = mfrCfg.models[mdlKey];
    if (!modelCfg) {
      throw new Error(`Unknown model: ${model}. Expected: ${Object.keys(mfrCfg.models).join(', ')}`);
    }
    newPriceEur = modelCfg.new_price_eur;
    brandKey = mfrCfg.brand_key;
  }

  // Initial used factor
  const initialUsedFactor = DEPRECIATION.initial_used_factor;
  const usedBaselineEur = newPriceEur * initialUsedFactor;

  // Age depreciation
  const ageCfg = DEPRECIATION.age;
  const ageYears = computeAgeYears(domYear);
  const ageValueEur = ageDepreciationEur(ageYears, usedBaselineEur, ageCfg);
  const ageFactor = newPriceEur > 0 ? ageValueEur / newPriceEur : 0;
  adjustments.ageFactor = Math.round(ageFactor * 1000) / 1000;

  // Jump depreciation
  const jumpCfg = DEPRECIATION.jumps;
  const jumpFloor = newPriceEur > 0 ? ageCfg.floor_value_eur / newPriceEur : 0.05;
  let jumpFactor;

  if (jumps != null) {
    jumpFactor = degressiveFactor(jumps, jumpCfg.horizon_jumps, jumpCfg.power, jumpFloor);
  } else {
    const estimatedJumps = ageYears > 0 ? estimateJumpsFromAge(ageYears, jumpCfg.estimate_cap, jumpCfg.estimate_tau) : 0;
    jumpFactor = degressiveFactor(estimatedJumps, jumpCfg.horizon_jumps, jumpCfg.power, jumpFloor);
    notes.push(`Jumps unknown. Estimated ~${Math.round(estimatedJumps)} (degressive, cap ~${jumpCfg.estimate_cap}).`);
  }
  adjustments.jumpFactor = Math.round(jumpFactor * 1000) / 1000;

  // Combine: worse-dominant
  const secondaryWeight = DEPRECIATION.combination.secondary_weight;
  let valueEur;

  if (jumpFactor < ageFactor) {
    valueEur = usedBaselineEur * jumpFactor;
    const agePenalty = (1 - ageFactor) * secondaryWeight;
    valueEur *= (1 - agePenalty);
  } else {
    valueEur = ageValueEur;
    const jumpPenalty = (1 - jumpFactor) * secondaryWeight;
    valueEur *= (1 - jumpPenalty);
  }

  const combinedFactor = newPriceEur > 0 ? valueEur / newPriceEur : 0;
  adjustments.combinedFactor = Math.round(combinedFactor * 1000) / 1000;

  // Brand premium
  let brandMult;
  if (customNewPriceEur) {
    brandMult = 1.0;
  } else {
    brandMult = ADJUSTMENTS.brand_premium[brandKey] ?? ADJUSTMENTS.brand_premium._default;
  }
  if (brandMult !== 1.0) {
    adjustments.brandPremium = brandMult;
  }

  // Container size adjustment (tiered)
  let sizeMult = 1.0;
  for (const tier of ADJUSTMENTS.container_size.tiers) {
    if (tier.max_above != null && maxCanopySqft != null && maxCanopySqft > tier.max_above) {
      sizeMult = tier.penalty;
      notes.push(`Container ${tier.label}. Size penalty ${Math.round((1 - tier.penalty) * 100)}%.`);
      break;
    }
    if (tier.min_below != null && minCanopySqft != null && minCanopySqft < tier.min_below) {
      sizeMult = tier.penalty;
      notes.push(`Container ${tier.label}. Size penalty ${Math.round((1 - tier.penalty) * 100)}%.`);
      break;
    }
  }
  if (sizeMult !== 1.0) {
    adjustments.containerSize = sizeMult;
  }

  // Harness size adjustment (graduated)
  let harnessMult = 1.0;
  for (const tier of ADJUSTMENTS.harness_size.tiers) {
    const hMatch = heightCm != null && heightCm >= tier.height_min;
    const wMatch = weightKg != null && weightKg >= tier.weight_min;
    if (hMatch || wMatch) {
      harnessMult = tier.penalty;
      const reasons = [];
      if (hMatch) reasons.push(`${heightCm}cm`);
      if (wMatch) reasons.push(`${weightKg}kg`);
      notes.push(`Harness ${tier.label} (${reasons.join(', ')}). Penalty ${Math.round((1 - tier.penalty) * 100)}%.`);
      break;
    }
  }
  if (harnessMult !== 1.0) {
    adjustments.harnessSize = harnessMult;
  }

  // Final value
  const totalAdjustment = brandMult * sizeMult * harnessMult;
  valueEur = valueEur * totalAdjustment;

  // Round to nearest 10
  valueEur = roundToNearest(valueEur, 10);
  valueEur = Math.max(valueEur, ageCfg.floor_value_eur);

  const retentionPct = newPriceEur > 0 ? Math.round((valueEur / newPriceEur) * 1000) / 10 : 0;

  return {
    valueEur,
    newPriceEur: Math.round(newPriceEur * 100) / 100,
    retentionPct,
    notes,
    adjustments,
  };
};
